import { Component, input } from '@angular/core';
import { AiEditChange } from '@domain';
import { TranslatePipe } from '@ngx-translate/core';
import { RaIconComponent } from 'ra-ui';

/**
 * Checklist das alterações de um pedido: pendente (círculo vazio) → em andamento
 * (girando) → aplicada (check verde). Conflito (laranja) e falha (vermelho) esperam o
 * usuário nos cartões abaixo do checklist; "mantida" é o conflito em que ele ficou com a
 * versão da pessoa.
 */
@Component({
    selector: 'ai-edit-checklist',
    imports: [RaIconComponent, TranslatePipe],
    template: `
        <ul class="checklist" [attr.aria-label]="'document.edit.aiEdit.checklistLabel' | translate">
            @for (change of changes(); track change.id) {
                <li class="op" [class]="'op op--' + change.status">
                    <span
                        class="op-icon"
                        role="img"
                        [attr.aria-label]="'document.edit.aiEdit.changeStatus.' + change.status | translate">
                        @switch (change.status) {
                            @case ('applied') {
                                <ra-icon icon="tick" varColor="contentInverse" [size]="10" />
                            }
                            @case ('conflict') {
                                <span aria-hidden="true">!</span>
                            }
                            @case ('error') {
                                <span aria-hidden="true">×</span>
                            }
                            @case ('kept') {
                                <span aria-hidden="true">–</span>
                            }
                        }
                    </span>
                    <span class="op-label">{{ change.label }}</span>
                    @if (change.where) {
                        <span class="op-where">{{ change.where }}</span>
                    }
                </li>
            }
        </ul>
    `,
    styles: `
        :host {
            display: block;
        }

        .checklist {
            display: flex;
            flex-direction: column;
            gap: 0.125rem;
            margin: 0;
            padding: 0.375rem;
            list-style: none;
            border: 1px solid var(--borderSecondary);
            border-radius: 0.75rem;
            background: var(--backgroundPrimary);
        }

        .op {
            display: flex;
            align-items: center;
            gap: 0.625rem;
            padding: 0.5rem 0.625rem;
            border-radius: 0.5rem;
            font-size: 0.8125rem;
            line-height: 1.35;
            color: var(--contentPrimary);
            transition: background-color 0.2s ease, color 0.2s ease;
        }

        .op-icon {
            flex: none;
            width: 1.125rem;
            height: 1.125rem;
            display: grid;
            place-items: center;
            box-sizing: border-box;
            border: 1.5px solid var(--borderPrimary);
            border-radius: 50%;
            font-size: 0.6875rem;
            font-weight: 700;
        }

        .op-label {
            flex: 1;
            min-width: 0;
        }

        .op-where {
            flex: none;
            max-width: 45%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 0.6875rem;
            color: var(--contentTertiary);
        }

        .op--pending {
            color: var(--contentTertiary);
        }

        .op--running {
            background: var(--primaryLight);

            .op-icon {
                border-color: var(--primary);
                border-top-color: transparent;
                animation: op-spin 0.8s linear infinite;
            }
        }

        .op--applied {
            color: var(--contentSecondary);

            .op-icon {
                border-color: var(--accentGreen);
                background: var(--accentGreen);
            }
        }

        .op--conflict {
            background: var(--accentOrangeLight);

            .op-icon {
                border-color: var(--accentOrange);
                color: var(--accentOrange);
            }
        }

        .op--error {
            background: var(--accentRedLight);

            .op-icon {
                border-color: var(--accentRed);
                color: var(--accentRed);
            }
        }

        .op--kept {
            color: var(--contentTertiary);

            .op-icon {
                border-color: var(--borderPrimary);
                color: var(--contentTertiary);
            }
        }

        @keyframes op-spin {
            to {
                transform: rotate(360deg);
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .op--running .op-icon {
                animation: none;
                border-top-color: var(--primary);
            }
        }
    `,
})
export class AiEditChecklistComponent {
    public changes = input.required<AiEditChange[]>();
}
